import { Button, Card, DatePicker, Form, Grid, Input, Message, Modal, Select, Space, Tabs } from "@arco-design/web-react"
import { CustomerAPI, SOAPI, InvoiceAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { useCallback, useEffect, useState } from "react";
import { Currency } from "@/components/Numeric";
import { InputNumber, InputCurrency } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { TermsDef } from "@/enums/common";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { SOStatus } from "@/enums/sales";
import { AccountType } from "@/enums/accounting";
import CustomerSelect from "@/components/CustomerSelect";
import AccountSelect from "@/components/AccountSelect";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { formatDate, TermUtil, today } from "@/utils";
import _ from "lodash";
import Decimal from "decimal.js";
import { useNavigate } from "react-router-dom";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;
const TabPane = Tabs.TabPane;

export const InvoiceBlank = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [soList, setSOList] = useState();
    const [items, setItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);

    const calcDueDate = useCallback(() => {
        const date = form.getFieldValue("invoiceDate");
        const term = form.getFieldValue("term");
        form.setFieldValue("dueDate", formatDate(TermUtil.dueDate(date, term)));
    }, [form]);

    const getItems = useCallback(async () => {
        const nonInventoryItems = await ItemAPI.getNonInventoryItems();
        let items = _.sortBy(nonInventoryItems, "itemName");
        setItems(items);
    }, [])

    const onCustomerSelect = useCallback(async (val) => {
        const customer = await CustomerAPI.get(val);
        form.setFieldValue("address", `${customer.billingAddress}, ${customer.billingCity}, ${customer.billingState} ${customer.billingZip}`)
        form.setFieldValue("term", customer.term);
        calcDueDate();

        // Check for past due invoices
        try {
            const pastDueAmount = await InvoiceAPI.getTotalPastDue({ customerId: val });
            if (pastDueAmount && pastDueAmount > 0) {
                await new Promise((resolve) => {
                    Modal.confirm({
                        title: 'Warning',
                        content: 'This customer has past due invoice. Make sure to get authorization from your manager before proceed!',
                        okText: 'Confirm',
                        hideCancel: true,
                        onOk: () => resolve(true),
                    });
                });
            }
        } catch (error) {
            console.error("Error checking past due invoices:", error);
        }
    }, [form, calcDueDate])

    const onItemSelect = async (field, val) => {
        const customerId = form.getFieldValue("customerId");
        const soItem = await InvoiceAPI.getCustomerLastInvoiceItem(customerId, val);
        const item = await ItemAPI.get(val);
        form.setFieldValue(`${field}.description`, item.description);
        form.setFieldValue(`${field}.uom`, item.uom);
        form.setFieldValue(`${field}.cost`, soItem ? soItem.cost : item.salePrice);
    }

    const onItemChange = (field) => {
        const quantity = new Decimal(form.getFieldValue(`${field}.quantity`));
        const cost = new Decimal(form.getFieldValue(`${field}.cost`));
        const amount = cost.mul(quantity);
        form.setFieldValue(`${field}.amount`, amount);
        calcTotal();
    }

    const onRevenueAmountChange = (val) => {
        calcTotal();
    }

    const calcTotal = useCallback(() => {
        const items = form.getFieldValue("items");
        const revenues = form.getFieldValue("revenues");
        let totalItems = new Decimal(0);
        let totalRevenues = new Decimal(0);
        for (const item of items ?? []) {
            totalItems = totalItems.plus(item.amount);
        }
        for (const revenue of revenues ?? []) {
            totalRevenues = totalRevenues.plus(revenue.amount);
        }
        setTotalItems(totalItems.toNumber());
        setTotalRevenue(totalRevenues.toNumber());
        form.setFieldValue("totalAmount", totalItems.plus(totalRevenues));
    }, [form]);

    const getCurrentSOs = useCallback(async (soNo) => {
        const values = await SOAPI.query(1, {
            soNo: soNo,
            status: [
                SOStatus.Created, SOStatus.Issued, SOStatus.Picked, SOStatus.Partial,
                SOStatus.PartialFulfilledInvoiced, SOStatus.ShortClosed, SOStatus.Fulfilled,
                SOStatus.FulfilledPartiallyInvoiced, SOStatus.FulfilledInvoiced,
                SOStatus.PartiallyInvoiced, SOStatus.Invoiced
            ]
        });
        setSOList(values.records);
    }, [])

    useEffect(() => {
        getCurrentSOs();
    }, [getCurrentSOs])

    useEffect(() => {
        getItems();
        form.setFieldsValue({ invoiceDate: today() });
    }, [getItems, form]);

    const save = async () => {
        try {
            const values = await form.validate();
            setSaving(true);
            const invoiceId = await InvoiceAPI.add(values);
            Message.success("Invoice created successfully");
            navigate(`/workspace/sales/invoice/${invoiceId}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                if (error.status === "909001") {
                    Message.warning(error.message);
                } else {
                    Message.error(error.message || "Failed to create Invoice");
                }
            }
        } finally {
            setSaving(false);
        }
    }

    return (
        <Card title="Add New Invoice" extra={
            <Space>
                <Button type="primary" onClick={() => save()} loading={saving}>Save</Button>
                <Button onClick={() => navigate("/workspace/sales/invoice")}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>

                <Row gutter={12}>
                    <Col span={12}>
                        <FormItem field="customerId" label="Customer">
                            <CustomerSelect onChange={(val) => onCustomerSelect(val)} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Invoice Date" field="invoiceDate"
                            labelCol={{ span: 10 }}
                            wrapperCol={{ span: 14 }}
                            required rules={[{ required: true }]}>
                            <DatePicker onChange={() => calcDueDate()} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem field="soId" label="SO#"
                            labelCol={{ span: 10 }}
                            wrapperCol={{ span: 14 }}
                        >
                            <Select options={_.map(soList, (item) => ({ value: item.id, label: item.soNo }))} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col span={12}>
                        <FormItem label="Address" field="address">
                            <Input readOnly />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Terms" field="term"
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}
                            required rules={[{ required: true }]}>
                            <EnumSelect enumObj={TermsDef} onChange={(val) => {
                                calcDueDate();
                            }} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Due Date" field="dueDate"
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}
                            required rules={[{ required: true }]}>
                            <DatePicker />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col span={18}>
                        <FormItem label="Reference#" field="reference"
                            labelCol={{ span: 4 }}>
                            <Form.List field="reference">
                                {(fields, { add, remove, move }) =>
                                    <>
                                        {fields.map((item, index) => {
                                            return (
                                                <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                    <Space className="form-item-row">
                                                        <FormItem field={item.field + ".type"}>
                                                            <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".number"}>
                                                            <Input />
                                                        </FormItem>
                                                        <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                    </Space>
                                                </FormItem>
                                            )
                                        })}
                                        <Button type="text" size="mini" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                    </>
                                }
                            </Form.List>
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem field="totalAmount" label="Total Amount"
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }} >
                            <InputCurrency readOnly />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <FormItem label="Memo" field="memo"
                            labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}>
                            <TextArea />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Tabs defaultActiveTab="1" type="card-gutter" lazyload={false}>
                            <TabPane key="1" title={
                                <Space size={80}>
                                    <span>Items:</span>
                                    <span><Currency value={totalItems} /></span>
                                </Space>
                            }>

                                <Form.List field="items">
                                    {(fields, { add, remove, move }) =>
                                        <>
                                            <table style={{ width: "100%" }}>
                                                <thead>
                                                    <tr>
                                                        <th width="250">Item</th>
                                                        <th>Description</th>
                                                        <th width="120">Qty</th>
                                                        <th width="70">U/M</th>
                                                        <th width="120">Price</th>
                                                        <th width="120">Amount</th>
                                                        <th width="50"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <FormItem field={`${item.field}.id`} hidden>
                                                                    <Input />
                                                                </FormItem>
                                                                <FormItem field={`${item.field}.itemId`} noStyle>
                                                                    <Select showSearch options={_.map(items, (item) => ({ value: item.id, label: item.itemName }))} onChange={(val) => onItemSelect(item.field, val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.description`} noStyle>
                                                                    <Input />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.quantity`} noStyle>
                                                                    <InputNumber onChange={(val) => onItemChange(item.field, val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.uom`} noStyle>
                                                                    <Input readOnly />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.cost`} noStyle>
                                                                    <InputNumber onChange={(val) => onItemChange(item.field, val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.amount`} noStyle>
                                                                    <InputNumber readOnly />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <Button type="text" icon={<IconDelete />}
                                                                    status="danger"
                                                                    onClick={() => remove(index)} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <Button type="text" size="mini" icon={<IconPlus />} style={{ margin: "10px" }}
                                                onClick={() => add()}>Add</Button>
                                        </>
                                    }
                                </Form.List>
                            </TabPane>
                            <TabPane key="2" title={
                                <Space size={80}>
                                    <span>Revenue:</span>
                                    <span><Currency value={totalRevenue} /></span>
                                </Space>
                            }>
                                <Form.List field="revenues">
                                    {(fields, { add, remove, move }) =>
                                        <>
                                            <table style={{ width: "100%" }}>
                                                <thead>
                                                    <tr>
                                                        <th width="250">Account</th>
                                                        <th width="200">Amount</th>
                                                        <th>Memo</th>
                                                        <th width="50"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <FormItem field={`${item.field}.id`} hidden>
                                                                    <Input />
                                                                </FormItem>
                                                                <FormItem field={`${item.field}.accountId`} rules={[{ required: true }]} noStyle>
                                                                    <AccountSelect type={AccountType.Income} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.amount`} rules={[{ required: true }]} noStyle>
                                                                    <InputNumber onChange={(val) => onRevenueAmountChange(val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.memo`} noStyle>
                                                                    <Input />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <Button type="text" icon={<IconDelete />}
                                                                    status="danger"
                                                                    onClick={() => remove(index)} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <Button type="text" size="mini" icon={<IconPlus />} style={{ margin: "10px" }}
                                                onClick={() => add()}>Add</Button>
                                        </>
                                    }
                                </Form.List>
                            </TabPane>

                        </Tabs>
                    </Col>
                </Row>
            </Form>
        </Card>
    )
}
